<template lang="pug">
  .log-tracking-container(:class="{ 'calendar-active': showCalendarPicker }")
    .log-tracking-title-container
      .back-button(v-if="showActivityForm || showAddPostForm" @click="handleBackClick")
        WtSvgIcon(icon="back-arrow" width="16px" height="13px")
      .log-tracking-title
        span.log-tracking-text {{ getModalTitle() }}
      .visibility-picker(v-if="showVisibilityPicker")
        BTooltip(
          v-if="isAlwaysPublicActivity"
          :label="`${selectedActivity.name} are always public`"
          position="is-left"
          type="is-dark"
        )
          WtSvgIcon(icon="eye" width="24px" height="24px")
        BDropdown.visibility-menu(
          v-else
          position="is-bottom-left"
          aria-role="menu"
        )
          a.visibility-trigger(
            slot="trigger"
            role="button"
            aria-label="Post visibility"
          )
            WtSvgIcon(:key="visibilityIcon" :icon="visibilityIcon" width="24px" height="24px")
          BDropdownItem.visibility-option(
            v-for="option in visibilityOptions"
            :key="option.code"
            aria-role="menuitem"
            :class="{ 'is-selected': option.code === postVisibility }"
            @click="changeVisibility(option.code)"
          )
            span {{ option.label }}
      .log-tracking-close-btn(
        @click="closeActivityTracker"
        :class="{ 'absolutely-positioned': !showActivityForm && !showAddPostForm }"
        )
        WtSvgIcon(icon="cross-icon" width="28px" height="28px")

    // Calendar View for activities that need date selection first
    .calendar-container(v-if="showCalendar && !showActivityForm && !showAddPostForm")
      .selected-date-container(
        @click="toggleCalendarPicker"
        :class="{ 'disabled': !isSelectedDateValid }"
      )
        span.selected-date-text {{ formatSelectedDate() }}
        span.calendar-icon(v-html="require('!!html-loader!@/assets/icons/calendar.svg')")

      .calendar-picker(v-if="showCalendarPicker")
        .calendar-header
          select.month-selector(v-model="selectedMonth")
            option(v-for="(month, index) in months" :key="index" :value="index") {{ month }}
          select.year-selector(v-model="selectedYear")
            option(v-for="year in years" :key="year" :value="year") {{ year }}

        .calendar-grid
          .calendar-weekdays
            .weekday(v-for="day in weekdays" :key="day.id") {{ day.label }}

          .calendar-dates
            .date-cell(
              v-for="date in calendarDates"
              :key="date.key"
              :class="{ 'selected': date.isSelected, 'other-month': date.isOtherMonth, 'future-date': date.isFutureDate, 'before-min-date': date.isBeforeMinDate }"
              @click="selectDate(date)"
            ) {{ date.day }}

      .next-button-container
        button.next-button(@click="proceedToActivityForm") Next

    // Dynamic activity form component
    .activity-form-container(v-if="showActivityForm")

      component(
        :is="getActivityComponent()"
        :entry-date="getFormattedDate()"
        :entry-item="activityEntryItem"
        :form-data="formData"
        :save-entry-wrapper="saveEntryWrapper"
        :unique-key="selectedActivity.component + uniqueKey"
        :disable-save="disableSave"
        :disable-reset="disableReset"
        :preselect-activity-name="editingActivityName"
        @formDataUpdated="handleFormDataUpdated"
        @formReset="handleFormReset"
        @entry-saved="handleEntrySaved"
      )

    // Optional post step shown after a metric form is submitted
    .add-post-form-container(v-if="showAddPostForm")
      AddPostOptional(
        :key="`add-post-${uniqueKey}`"
        :activity-name="selectedActivity.name"
        :submitting="submittingAddPost"
        :initial-text="existingFeedPostText"
        :initial-image="existingFeedPostImage"
        @submit-post="handleAddPostSubmit"
      )

    // Activity Grid View
    .log-tracking-body-container(v-if="!selectedActivity")
      .icons-grid
        .icons-row(v-for="(row, rowIndex) in activityRows" :key="rowIndex")
          .activity-container.flex-container(
            v-for="activity in row"
            :key="activity.id"
            @click="selectActivity(activity)"
          )
            span.activity-icon(v-html="require(`!!html-loader!@/assets/icons/${activity.icon}`)")
            span.activity-text {{ activity.name }}

      .post-section(v-if="coreProgramMeta.converter")
        .activity-container.flex-container(@click="selectActivity(postActivity)")
          span(v-html="require('!!html-loader!@/assets/icons/post.svg')")
          span.activity-text Post
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { dateUtilityMixin } from "@/mixins/dateUtilityMixin";
import analyticsTrackingMixin from "@/mixins/analyticsTrackingMixin";
import WtSvgIcon from "@@/WtSvgIcon/WtSvgIcon.vue";
import LogWeight from "./LogWeight.vue";
import LogSteps from "./LogSteps.vue";
import LogHeartRate from "./LogHeartRate.vue";
import LogNutrition from "./LogNutrition.vue";
import LogSleep from "./LogSleep.vue";
import LogWater from "./LogWater.vue";
import LogMindfulness from "./LogMindfulness.vue";
import LogActivities from "./LogActivities.vue";
import LogPost from "./LogPost.vue";
import LogMood from "./LogMood.vue";
import AddPostOptional from "./AddPostOptional.vue";

const VISIBILITY_OPTIONS = [
  { code: 0, label: "Only Me", icon: "eye-off" },
  { code: 1, label: "Friends", icon: "eye-friends" },
  { code: 2, label: "Anyone", icon: "eye" }
];

export default {
  name: "LogTrackingModal",

  ...mapGetters("program", ["coreProgramMeta"]),

  components: {
    WtSvgIcon,
    LogWeight,
    LogSteps,
    LogHeartRate,
    LogNutrition,
    LogSleep,
    LogWater,
    LogMindfulness,
    LogMood,
    LogActivities,
    LogPost,
    AddPostOptional
  },

  mixins: [analyticsTrackingMixin, dateUtilityMixin],

  props: {
    entryDate: {
      type: String,
      default: ""
    },
    autoSelectActivity: {
      type: String,
      default: null
    },
    canShowAllMetrices: {
      type: Boolean,
      default: false
    },
    challengeStartDate: {
      type: String,
      default: null
    },
    challengeEndDate: {
      type: String,
      default: null
    },

    editingPost: {
      type: Object,
      default: null
    }
  },

  data() {
    const today = new Date();

    // Determine initial selected date
    let initialDate = today;
    if (this.challengeEndDate) {
      const challengeEnd = new Date(this.challengeEndDate);

      if (challengeEnd < today) {
        initialDate = challengeEnd;
      }
    }

    // Editing an existing post: pin the date to the post's date
    if (this.editingPost && this.editingPost.postDate) {
      const postDate = new Date(
        `${String(this.editingPost.postDate).slice(0, 10)}T00:00:00`
      );
      if (!isNaN(postDate)) {
        initialDate = postDate;
      }
    }
    return {
      showCalendar: false,
      showCalendarPicker: false,
      showActivityForm: false,
      showAddPostForm: false,
      pendingSaveActions: null,
      submittingAddPost: false,
      existingFeedPost: null,
      postVisibility: 2,
      visibilityTouched: false,

      // FeedPost metricType/metricText values for each grid activity
      FEED_METRIC_MAP: {
        Steps: { type: "steps" },
        Activities: { type: "activities" },
        Nutrition: { type: "wellness", text: "nutrition" },
        Water: { type: "wellness", text: "water" },
        "Heart Rate": { type: "wellness", text: "heart" },
        Sleep: { type: "wellness", text: "sleep" },
        Weight: { type: "wellness", text: "weight" },
        Mindfulness: { type: "wellness", text: "mindfulness" },
        Mood: { type: "wellness", text: "mood" }
      },
      selectedActivity: null,
      selectedMonth: initialDate.getMonth(),
      selectedYear: initialDate.getFullYear(),
      selectedDate: initialDate.getDate(),
      formData: new FormData(),
      disableSave: true,
      disableReset: true,
      uniqueKey: "",
      entryItem: {},

      // Activity configuration with components
      ALL_ACTIVITIES: [
        {
          id: 1,
          name: "Steps",
          icon: "red-circle-shoe.svg",
          component: "LogSteps",
          needsDateFirst: true
        },
        {
          id: 2,
          name: "Nutrition",
          icon: "nutrition.svg",
          component: "LogNutrition",
          needsDateFirst: true
        },
        {
          id: 3,
          name: "Activities",
          icon: "activities-builder.svg",
          component: "LogActivities",
          needsDateFirst: true
        },
        {
          id: 4,
          name: "Water",
          icon: "water.svg",
          component: "LogWater",
          needsDateFirst: true
        },
        {
          id: 5,
          name: "Heart Rate",
          icon: "heart_rate.svg",
          component: "LogHeartRate",
          needsDateFirst: true
        },
        {
          id: 6,
          name: "Sleep",
          icon: "sleep.svg",
          component: "LogSleep",
          needsDateFirst: true
        },
        {
          id: 7,
          name: "Weight",
          icon: "monitor_weight.svg",
          component: "LogWeight",
          needsDateFirst: true
        },
        {
          id: 8,
          name: "Mindfulness",
          icon: "mindfulness.svg",
          component: "LogMindfulness",
          needsDateFirst: true
        },
        {
          id: 9,
          name: "Mood",
          icon: "mood.svg",
          component: "LogMood",
          needsDateFirst: true
        },
        {
          id: 10,
          name: "Post",
          icon: "post.svg",
          component: "LogPost",
          needsDateFirst: true
        }
      ],

      postActivity: {
        id: 10,
        name: "Post",
        icon: "post.svg",
        component: "LogPost",
        needsDateFirst: true
      },

      months: [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December"
      ],

      weekdays: [
        { id: 1, label: "S" },
        { id: 2, label: "M" },
        { id: 3, label: "T" },
        { id: 4, label: "W" },
        { id: 5, label: "T" },
        { id: 6, label: "F" },
        { id: 7, label: "S" }
      ]
    };
  },

  computed: {
    ...mapGetters("members", [
      "makeAnEntryJournal",
      "makeAnEntryJournalLoading"
    ]),
    ...mapGetters("program", ["coreProgramMeta"]),

    editingActivityName() {
      if (this.editingPost && this.editingPost.metricType === "activities") {
        return this.editingPost.metricText || null;
      }
      return null;
    },

    showVisibilityPicker() {
      return (
        (this.showActivityForm || this.showAddPostForm) &&
        this.selectedActivity &&
        this.selectedActivity.name !== "Post"
      );
    },

    isAlwaysPublicActivity() {
      return (
        this.selectedActivity &&
        ["Steps", "Activities"].includes(this.selectedActivity.name)
      );
    },

    visibilityOptions() {
      return VISIBILITY_OPTIONS;
    },

    visibilityIcon() {
      const selected = VISIBILITY_OPTIONS.find(
        option => option.code === this.postVisibility
      );
      return this._.get(selected, "icon", "eye");
    },

    existingFeedPostText() {
      return this._.get(this.existingFeedPost, "content", "") || "";
    },

    existingFeedPostImage() {
      return (
        this._.get(this.existingFeedPost, "largeImage", "") ||
        this._.get(this.existingFeedPost, "image", "") ||
        ""
      );
    },

    activityEntryItem() {
      if (this.selectedActivity && this.selectedActivity.name === "Post") {
        if (this.editingPost) {
          return {
            text: this.editingPost.content || "",
            visibility: this._.get(this.editingPost, "visibility.code", 2),
            image: this.editingPost.largeImage || this.editingPost.image || ""
          };
        }
        return {};
      }
      return this.entryItem;
    },

    activities() {
      return this.ALL_ACTIVITIES.filter(activity => {
        if (this.canShowAllMetrices) {
          return true;
        } else if (this.coreProgramMeta.converter) {
          return activity.name !== "Post";
        } else {
          return activity.name !== "Activities";
        }
      });
    },

    activityRows() {
      const rows = [];
      for (let i = 0; i < this.activities.length; i += 3) {
        rows.push(this.activities.slice(i, i + 3));
      }
      return rows;
    },

    years() {
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let i = currentYear - 5; i <= currentYear + 5; i++) {
        years.push(i);
      }
      return years;
    },

    minDate() {
      const twoWeeksAgo = this.moment()
        .subtract(14, "days")
        .startOf("day");
      const registered = this.moment(
        this.unixDateTime(this.$auth.user().registeredDate).dateObject
      ).startOf("day");

      if (this.challengeStartDate) {
        const challengeStart = this.moment(this.challengeStartDate).startOf(
          "day"
        );
        return registered.isAfter(challengeStart)
          ? registered._d
          : challengeStart._d;
      }

      return registered.isAfter(twoWeeksAgo) ? registered._d : twoWeeksAgo._d;
    },

    maxDate() {
      // If challenge end date is provided, use it as the maximum date
      if (this.challengeEndDate) {
        return this.moment(this.challengeEndDate).endOf("day")._d;
      }
      // Otherwise, allow up to today
      return this.moment().endOf("day")._d;
    },

    calendarDates() {
      const year = this.selectedYear;
      const month = this.selectedMonth;
      const firstDay = new Date(year, month, 1);
      const startDate = new Date(firstDay);
      startDate.setDate(startDate.getDate() - firstDay.getDay());

      const dates = [];
      const currentDate = new Date(startDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to compare only dates

      const minDateObj = new Date(this.minDate);
      minDateObj.setHours(0, 0, 0, 0);

      const maxDateObj = new Date(this.maxDate);
      maxDateObj.setHours(23, 59, 59, 999);

      for (let week = 0; week < 6; week++) {
        for (let day = 0; day < 7; day++) {
          const isCurrentMonth = currentDate.getMonth() === month;
          const isSelected =
            isCurrentMonth &&
            currentDate.getDate() === this.selectedDate &&
            currentDate.getMonth() === this.selectedMonth &&
            currentDate.getFullYear() === this.selectedYear;

          // Check if date is in the future
          const dateOnly = new Date(currentDate);
          dateOnly.setHours(0, 0, 0, 0);
          const isFutureDate = dateOnly > today || dateOnly > maxDateObj;

          // Check if date is before min date (registration date or 2 weeks ago)
          const isBeforeMinDate = dateOnly < minDateObj;

          dates.push({
            day: currentDate.getDate(),
            date: new Date(currentDate),
            isSelected: isSelected,
            isOtherMonth: !isCurrentMonth,
            isFutureDate: isFutureDate,
            isBeforeMinDate: isBeforeMinDate,
            key: `${currentDate.getFullYear()}-${currentDate.getMonth()}-${currentDate.getDate()}`
          });

          currentDate.setDate(currentDate.getDate() + 1);
        }
      }

      return dates;
    },

    isSelectedDateValid() {
      const selectedDateObj = new Date(
        this.selectedYear,
        this.selectedMonth,
        this.selectedDate
      );
      selectedDateObj.setHours(0, 0, 0, 0);

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const minDateObj = new Date(this.minDate);
      minDateObj.setHours(0, 0, 0, 0);

      return selectedDateObj <= today && selectedDateObj >= minDateObj;
    }
  },

  watch: {
    selectedMonth() {
      this.fetchEntryItem();
    },
    selectedYear() {
      this.fetchEntryItem();
    },
    selectedDate() {
      this.fetchEntryItem();
    },
    activities: {
      handler(newActivities) {
        if (
          newActivities &&
          newActivities.length > 0 &&
          this.autoSelectActivity
        ) {
          this.$nextTick(() => {
            const activity = this.activities.find(
              activity => activity.name === this.autoSelectActivity
            );
            if (activity) {
              this.selectActivity(activity);
            }
          });
        }
      },
      immediate: true
    }
  },

  methods: {
    ...mapActions(["snackBar"]),
    ...mapActions("members", [
      "fetchMakeAnEntryJournalByDate",
      "storeJournal",
      "updateJournal"
    ]),
    ...mapActions("feed", [
      "fetchUserFeedPosts",
      "fetchFeedPostsByDate",
      "updateFeedPost"
    ]),

    closeActivityTracker() {
      this.$emit("closeActivityTracker");
    },

    getModalTitle() {
      if (this.showAddPostForm) {
        return "Add Post (Optional)";
      }
      if (
        (this.showActivityForm && this.selectedActivity) ||
        this.showCalendar
      ) {
        if (this.selectedActivity.name === "Post") {
          return this.editingPost ? "Edit post" : "Add a post";
        }
        return this.editingPost
          ? `Edit ${this.selectedActivity.name}`
          : `Log ${this.selectedActivity.name}`;
      }
      return "Log Tracking";
    },

    changeVisibility(visibility) {
      this.postVisibility = visibility;
      this.visibilityTouched = true;
    },

    selectActivity(activity) {
      this.selectedActivity = activity;
      this.uniqueKey = `${activity.component}-${Date.now()}`;
      this.postVisibility = this.editingPost
        ? this._.get(this.editingPost, "visibility.code", 2)
        : 2;
      this.visibilityTouched = false;

      // Editing: the date is pinned to the post's date, skip the calendar
      if (this.editingPost) {
        this.showCalendar = false;
        this.showCalendarPicker = false;
        this.showActivityForm = true;
        this.fetchEntryItem();
        return;
      }

      if (activity.needsDateFirst) {
        // Show calendar first for activities that need date selection
        this.showCalendar = true;
        this.showCalendarPicker = false;
        this.showActivityForm = false;
      } else {
        // Go directly to the activity form (like Weight)
        this.showCalendar = false;
        this.showActivityForm = true;
        this.fetchEntryItem();
      }
    },

    async proceedToActivityForm() {
      // Move from calendar to activity form
      this.showActivityForm = true;
      this.showCalendarPicker = false;

      // Fetch entry data for the selected date
      await this.fetchEntryItem();

      await this.syncVisibilityFromExistingPost();
    },

    async syncVisibilityFromExistingPost() {
      if (
        !this.selectedActivity ||
        this.selectedActivity.name === "Post" ||
        this.isAlwaysPublicActivity ||
        this.visibilityTouched
      ) {
        return;
      }

      const existingPost = await this.fetchExistingFeedPost();

      if (existingPost && !this.visibilityTouched) {
        this.postVisibility = this._.get(existingPost, "visibility.code", 2);
      }
    },

    goBackToCalendar() {
      this.showActivityForm = false;
      this.showCalendarPicker = false;
      this.formData = new FormData();
      this.disableSave = true;
      this.disableReset = true;
      this.visibilityTouched = false;
    },

    goBack() {
      // Go back to main activity selection
      this.showCalendar = false;
      this.showActivityForm = false;
      this.showAddPostForm = false;
      this.pendingSaveActions = null;
      this.submittingAddPost = false;
      this.existingFeedPost = null;
      this.selectedActivity = null;
      this.showCalendarPicker = false;
      this.formData = new FormData();
      this.disableSave = true;
      this.disableReset = true;
    },

    handleBackClick() {
      if (this.showAddPostForm) {
        // Go back from the post step to the metric form, keeping its data
        this.showAddPostForm = false;
        this.pendingSaveActions = null;
        this.existingFeedPost = null;
        this.showActivityForm = true;
        return;
      }
      // No calendar step to return to when editing a post
      if (this.editingPost) {
        this.closeActivityTracker();
        return;
      }
      this.goBackToCalendar();
    },

    toggleCalendarPicker() {
      if (this.isSelectedDateValid) {
        this.showCalendarPicker = !this.showCalendarPicker;
        this.$emit("calendarPickerToggle", this.showCalendarPicker);
      }
      return;
    },

    selectDate(dateObj) {
      // Prevent selection of future dates, other month dates, or dates before min date
      if (
        !dateObj.isFutureDate &&
        !dateObj.isOtherMonth &&
        !dateObj.isBeforeMinDate
      ) {
        this.selectedDate = dateObj.day;
        this.selectedMonth = dateObj.date.getMonth();
        this.selectedYear = dateObj.date.getFullYear();
        this.showCalendarPicker = false;
        this.$emit("calendarPickerToggle", false);
        this.fetchEntryItem();
      }
    },

    async fetchEntryItem() {
      const date = this.date(
        new Date(this.selectedYear, this.selectedMonth, this.selectedDate)
      );

      const payload = {
        memberId: this.$auth.user().id,
        date: date.formatted
      };

      try {
        await this.fetchMakeAnEntryJournalByDate(payload);

        if (Array.isArray(this.makeAnEntryJournal)) {
          this.entryItem = this.makeAnEntryJournal[0] || {};
        } else if (
          this.makeAnEntryJournal &&
          typeof this.makeAnEntryJournal === "object"
        ) {
          this.entryItem = this.makeAnEntryJournal;
        } else {
          this.entryItem = {};
        }
      } catch (error) {
        console.error("Error fetching entry item:", error);
        this.entryItem = {};
      }
    },

    getActivityComponent() {
      return this.selectedActivity ? this.selectedActivity.component : null;
    },

    getFormattedDate() {
      return this.date(
        new Date(this.selectedYear, this.selectedMonth, this.selectedDate)
      ).formatted;
    },

    formatSelectedDate() {
      const date = new Date(
        this.selectedYear,
        this.selectedMonth,
        this.selectedDate
      );
      const dayNames = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday"
      ];
      const monthNames = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December"
      ];

      const dayName = dayNames[date.getDay()];
      const day = date.getDate();
      const month = monthNames[date.getMonth()];
      const suffix = this.getOrdinalSuffix(day);

      return `${dayName} ${day}${suffix} ${month}`;
    },

    getOrdinalSuffix(day) {
      if (day >= 11 && day <= 13) return "th";
      switch (day % 10) {
        case 1:
          return "st";
        case 2:
          return "nd";
        case 3:
          return "rd";
        default:
          return "th";
      }
    },

    handleFormDataUpdated() {
      this.disableSave = false;
      this.disableReset = false;
    },

    handleFormReset() {
      this.disableSave = true;
      this.disableReset = true;
    },

    handleEntrySaved() {
      this.$emit("activityLogged", {
        activity: this.selectedActivity.name,
        date: this.getFormattedDate()
      });
      if (this.editingPost) {
        this.closeActivityTracker();
      } else if (this.canShowAllMetrices) {
        this.closeActivityTracker();
        this.$emit("refreshPoints");
      } else {
        this.goBack();
      }
    },

    async saveEntryWrapper(storeAction, updateAction) {
      // Metric activities pause here so the user can attach an optional post;
      // the Post activity submits straight through.
      if (this.selectedActivity && this.selectedActivity.name !== "Post") {
        this.pendingSaveActions = { storeAction, updateAction };
        this.existingFeedPost = await this.fetchExistingFeedPost();

        if (this.existingFeedPost && !this.visibilityTouched) {
          this.postVisibility = this._.get(
            this.existingFeedPost,
            "visibility.code",
            2
          );
        }

        this.showActivityForm = false;
        this.showAddPostForm = true;
        return;
      }

      return this.performSave(storeAction, updateAction);
    },

    // Find this date's feed post for the selected metric so the post step
    // can prefill any text/image the user already attached to it.
    async fetchExistingFeedPost() {
      const metric = this.FEED_METRIC_MAP[this.selectedActivity.name];

      if (!metric) {
        return null;
      }

      const posts = await this.fetchFeedPostsByDate({
        date: this.date(
          new Date(this.selectedYear, this.selectedMonth, this.selectedDate),
          "DATE_FORMAT_DB"
        ).formatted
      });

      return (
        posts.find(post => {
          if (post.metricType !== metric.type) {
            return false;
          }
          return metric.text ? post.metricText === metric.text : true;
        }) || null
      );
    },

    async handleAddPostSubmit({ text, image, imageRemoved }) {
      if (!this.pendingSaveActions) {
        return;
      }

      // Send text when entered, or when clearing previously saved content
      if (text || this.existingFeedPostText) {
        this.formData.set("postText", text);
        this.formData.set("isPostTextUpdating", "1");
      }
      if (image) {
        this.formData.set("postImage", image);
        this.formData.set("isPostImageUpdating", "1");
      } else if (imageRemoved) {
        // Flag without a file clears the saved image on the backend
        this.formData.set("isPostImageUpdating", "1");
      }

      this.submittingAddPost = true;
      const { storeAction, updateAction } = this.pendingSaveActions;

      try {
        await this.performSave(storeAction, updateAction);
      } finally {
        this.submittingAddPost = false;
      }
    },

    async performSave(storeAction, updateAction) {
      this.formData.set(
        "date",
        this.date(
          new Date(this.selectedYear, this.selectedMonth, this.selectedDate),
          "DATE_FORMAT_DB"
        ).formatted
      );

      if (this.selectedActivity && this.selectedActivity.name !== "Post") {
        this.formData.set(
          "postVisibility",
          this.isAlwaysPublicActivity ? 2 : this.postVisibility
        );
      }

      const isEditingPlainPost =
        this.editingPost && this.selectedActivity.name === "Post";

      const payload = {
        itemId: isEditingPlainPost ? this.editingPost.id : this.entryItem.id,
        memberId: this.$auth.user().id,
        formData: this.formData
      };

      let response;

      try {
        if (isEditingPlainPost) {
          response = await this.updateFeedPost(payload);
        } else if (this._.isEmpty(this.entryItem)) {
          response = await storeAction(payload);
        } else {
          response = await updateAction(payload);
        }

        if (response.error) {
          let message = this._.get(response, "error.message", "error saving.");
          message = `Oh darn, there was an error: ${message} Please try again, or reach out to wellness@be-engaged.co.`;

          await this.snackBar({
            type: "is-danger",
            duration: 10000,
            queue: false,
            message
          });
        } else {
          await this.snackBar({
            message: this.editingPost ? `Post updated!` : `Tracking added!`
          });

          // Refresh auth user to update total points
          await this.$auth.fetch();

          // Refresh the dashboard feed so the new post shows immediately
          this.fetchUserFeedPosts({
            userId: this.$auth.user().id,
            page: null,
            resetList: true
          });

          // Reset form
          this.formData = new FormData();
          this.disableSave = true;
          this.disableReset = true;

          this.handleEntrySaved();
        }
      } catch (error) {
        console.error("Error saving entry:", error);
        await this.snackBar({
          type: "is-danger",
          duration: 10000,
          queue: false,
          message: "An unexpected error occurred. Please try again."
        });
      }
    }
  }
};
</script>

<style lang="sass" scoped>
.log-tracking-container
  background-color: white !important
  max-width: 455px
  margin: auto
  display: flex
  flex-direction: column
  gap: 20px
  position: relative
  overflow: visible
  border-radius: 16px

.log-tracking-title-container
  padding: 20px 16px
  display: flex
  flex-direction: row
  justify-content: space-between
  align-items: center
  border-bottom: 1px solid #E8EBEF
  position: relative

.log-tracking-text
  font-weight: 600
  line-height: 28px
  font-size: 18px
  letter-spacing: 0px
  color: #1B1D20

.log-tracking-title
  flex: 1
  display: flex
  justify-content: center

.visibility-picker
  display: flex
  align-items: center
  margin-right: 8px
  line-height: 0
  ::v-deep svg
    display: block
  ::v-deep .dropdown-trigger
    display: flex
    align-items: center
  ::v-deep .tooltip-content
    padding: 8px 12px
    border-radius: 8px
    font-size: 13px
    line-height: 1.4
  .visibility-trigger
    display: flex
    align-items: center
    cursor: pointer
  .visibility-menu
    ::v-deep .dropdown-content
      border-radius: 12px
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15)
      padding: 8px
    ::v-deep .dropdown-item
      border-radius: 8px
      text-align: center
      color: #2972F0
      font-size: 14px
      font-weight: 400
      line-height: 1.4
      padding: 8px 24px
      &.is-selected
        background: #C6DCFF

.log-tracking-close-btn
  cursor: pointer
  display: flex
  align-items: center
  justify-content: center
  width: 32px
  height: 32px
  border-radius: 50%
  transition: background-color 0.2s ease

  &.absolutely-positioned
    position: absolute
    top: 17px
    right: 12px

.log-tracking-body-container
  padding: 0 16px 16px
  display: flex
  flex-direction: column
  gap: 32px

.icons-grid
  display: flex
  flex-direction: column
  gap: 24px

.icons-row
  display: flex
  flex-direction: row
  justify-content: space-between
  gap: 16px

.post-section
  display: flex
  justify-content: center
  align-items: center

.activity-container
  display: flex
  flex-direction: column
  gap: 8px
  align-items: center
  justify-content: center
  min-width: 130px
  max-height: 62px
  cursor: pointer
  transition: all 0.2s ease
  padding: 12px 8px
  border-radius: 12px
  border: 2px solid transparent

  &:hover
    transform: translateY(-2px)

.flex-container
  display: flex

.activity-container span:first-child
  display: flex
  align-items: center
  justify-content: center
  width: 36px
  height: 36px
  flex-shrink: 0

.activity-text
  font-weight: 500
  font-size: 13px
  line-height: 18px
  letter-spacing: 0px
  text-align: center
  color: #495057

// Add overlay backdrop for calendar
.calendar-overlay
  position: fixed
  top: 0
  left: 0
  right: 0
  bottom: 0
  background-color: rgba(0, 0, 0, 0.1)
  z-index: 1999
  backdrop-filter: blur(2px)

// Calendar styles
.calendar-container
  padding: 0 16px 16px
  display: flex
  flex-direction: column
  gap: 20px
  width: 455px
  min-width: 354px
  position: relative

.selected-date-container
  position: relative
  background-color: #F1F3F8
  padding: 8px 12px
  border-radius: 10px
  display: flex
  justify-content: space-between
  align-items: center
  cursor: pointer
  transition: background-color 0.2s ease

  &:hover
    border-color: #4A90E2

  &.disabled
    cursor: not-allowed
    opacity: 0.6
    background-color: #F5F5F5

    .selected-date-text
      color: #9CA3AF

    .calendar-icon
      ::v-deep svg
        fill: #9CA3AF

    &:hover
      border-color: transparent

.selected-date-text
  color: #2972F0
  font-weight: 500
  font-size: 16px

.calendar-icon
  display: flex
  align-items: center
  justify-content: center
  width: 24px
  height: 24px

  ::v-deep svg
    fill: #4A90E2
    width: 20px
    height: 20px

.calendar-picker
  position: fixed
  top: 50%
  left: 50%
  transform: translate(-50%, 9%)
  width: 424px
  display: flex
  flex-direction: column
  gap: 20px
  background: #F1F3F8
  border-radius: 10px
  padding: 12px 12px 22px 12px
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15)

  max-height: 450px
  overflow: visible

.calendar-header
  display: flex
  gap: 16px
  justify-content: space-between
  align-items: fill

.month-selector,
.year-selector
  padding: 12px 16px
  border: 1px solid #D9DBE0
  border-radius: 8px
  font-size: 14px
  font-weight: 500
  background-color: white
  cursor: pointer
  transition: all 0.2s ease
  color: #333

.month-selector
  width: 200px
.year-selector
  width: 90px

.calendar-grid
  display: flex
  flex-direction: column
  gap: 8px

.calendar-weekdays
  display: grid
  grid-template-columns: repeat(7, 1fr)
  gap: 10px

.weekday
  text-align: center
  font-weight: 700
  font-size: 14px
  line-spacing: 0px
  line-height: 20px
  color: #1B1D20
  text-transform: uppercase

.calendar-dates
  display: grid
  grid-template-columns: repeat(7, 1fr)
  gap: 10px

.date-cell
  aspect-ratio: 1
  display: flex
  align-items: center
  justify-content: center
  cursor: pointer
  border-radius: 10px
  font-size: 14px
  font-weight: 600
  line-height: 20px
  line-spacing: 0px
  color: #333

  &:hover:not(.other-month):not(.selected):not(.future-date):not(.before-min-date)
    background-color: #F0F8FF
    color: #4A90E2
    transform: scale(1.05)

  &.selected
    background-color: #2972F0
    color: white
    font-weight: 600
    box-shadow: 0 2px 8px rgba(74, 144, 226, 0.3)

  &.other-month,
  &.future-date,
  &.before-min-date
    color: #D1D5DB
    cursor: not-allowed
    opacity: 0.4

    &:hover
      transform: none
      background-color: transparent
      color: #D1D5DB

.next-button-container
  display: flex
  justify-content: center

.next-button
  background-color: #2972F0
  color: white
  border: none
  padding: 14px 32px
  border-radius: 8px
  font-size: 16px
  font-weight: 600
  font-size: 16px
  cursor: pointer
  width: 100%
  letter-spacing: 0px

// Activity Form Container
.activity-form-container,
.add-post-form-container
  padding: 0 16px 16px
  display: flex
  flex-direction: column
  gap: 20px
  width: 455px
  min-width: 354px

.form-header
  display: flex
  align-items: center
  gap: 16px
  padding-bottom: 16px
  border-bottom: 1px solid #E8EBEF

.back-button
  display: flex
  align-items: center
  gap: 8px
  cursor: pointer
  color: #4A90E2
  font-weight: 500
  font-size: 14px
  padding: 8px 12px
  border-radius: 6px
  transition: all 0.2s ease

.form-title
  font-size: 18px
  font-weight: 600
  color: #1B1D20
  margin: 0
  flex: 1

.activity-form-container,
.add-post-form-container
  animation: slideIn 0.3s ease-out

.calendar-container
  animation: slideIn 0.3s ease-out

// Loading states
.activity-container.loading
  opacity: 0.6
  pointer-events: none

// Focus states for accessibility
.activity-container:focus,
.next-button:focus,
.back-button:focus
  outline: 2px solid #4A90E2
  outline-offset: 2px

.activity-icon
  ::v-deep svg
    width: 36px
    height: 36px

// Responsive Design
@media screen and (max-width: 768px)
  .log-tracking-container
    width: 356px
    max-width: 90vw

  .activity-container
    min-width: 93px
    max-height: 72px

  .icons-row
    gap: 12px

  .activity-text
    font-size: 12px

  .calendar-container,
  .activity-form-container,
  .add-post-form-container
    width: 100%
    min-width: auto
    padding: 0px 16px 16px

  .log-tracking-title-container
    padding: 20px 16px

  .log-tracking-text
    font-size: 16px

  .calendar-picker
    position: fixed
    top: 50%
    left: 50%
    width: 332px

  .selected-date-container
    padding: 14px 16px

  .selected-date-text
    font-size: 15px

@media screen and (max-width: 480px)
  .log-tracking-container
    width: 356px

  .activity-container
    min-width: 85px
    gap: 6px
    padding: 8px 4px

  .activity-container span:first-child
    width: 32px
    height: 32px

  .activity-text
    font-size: 11px
    line-height: 16px

  .icons-row
    gap: 8px

  .calendar-picker
    position: fixed
    top: 50%
    left: 50%
    padding: 12px

  .selected-date-text
    font-size: 14px

  .date-cell
    min-height: 36px
    font-size: 13px

  .month-selector,
  .year-selector
    padding: 10px 12px
    font-size: 13px

  .next-button
    padding: 12px 24px
    font-size: 14px

  .form-title
    font-size: 16px

  .back-button
    font-size: 13px
    padding: 6px 8px
</style>
